import React, { useState } from 'react';

const OVERALL_PROGRESS = { minimal: 'Minimal', moderate: 'Moderate', significant: 'Significant', resolved: 'Resolved', deferred: 'Deferred' };
const OBJECTIVE_STATUS = { continues: 'Continues', modified: 'Modified', resolved: 'Resolved', deferred: 'Deferred' };

function buildInitialGoals(plan) {
  const goals = [];
  for (const p of plan.problems || []) {
    for (const g of p.goals || []) {
      goals.push({
        goal_id: g.id, goal_description: g.description,
        overall_progress: 'moderate', notes: '',
        objectives: (g.objectives || []).map(o => ({
          objective_id: o.id, objective_description: o.description,
          status: 'continues', progress: '', regression: '', narrative: '', barriers: '', revised_target_date: '',
        })),
      });
    }
  }
  return goals;
}

// Shared per-goal / per-objective treatment-plan review editor. Emits the full review payload
// (including nested `goals`) so the backend persists append-only review entries.
export default function TreatmentPlanReviewForm({ plan, busy, onSubmit, onCancel }) {
  const [reviewType, setReviewType] = useState('review');
  const [reviewedAt, setReviewedAt] = useState(new Date().toISOString().slice(0, 16));
  const [nextDue, setNextDue] = useState('');
  const [notes, setNotes] = useState('');
  const [crisisRelapse, setCrisisRelapse] = useState('');
  const [dischargePlan, setDischargePlan] = useState('');
  const [goals, setGoals] = useState(() => buildInitialGoals(plan));

  const setGoal = (gi, patch) => setGoals(gs => gs.map((g, i) => i === gi ? { ...g, ...patch } : g));
  const setObj = (gi, oi, patch) => setGoals(gs => gs.map((g, i) => i === gi ? { ...g, objectives: g.objectives.map((o, j) => j === oi ? { ...o, ...patch } : o) } : g));

  function submit(e) {
    e.preventDefault();
    const payload = {
      review_type: reviewType, reviewed_at: reviewedAt, next_review_due_at: nextDue, notes,
      crisis_relapse: crisisRelapse, discharge_plan: dischargePlan,
      goals: goals.map(g => ({
        goal_id: g.goal_id, overall_progress: g.overall_progress, notes: g.notes,
        objectives: g.objectives.map(o => ({ objective_id: o.objective_id, status: o.status, progress: o.progress, regression: o.regression, narrative: o.narrative, barriers: o.barriers, revised_target_date: o.revised_target_date })),
      })),
    };
    onSubmit(payload);
  }

  return (
    <form className="php-form section-space" onSubmit={submit}>
      <div className="content-heading"><div><h5>Record review</h5></div></div>
      <div className="form-grid">
        <label>Review type<select value={reviewType} onChange={e => setReviewType(e.target.value)}><option value="review">Review</option><option value="recertification">Recertification</option></select></label>
        <label>Reviewed at<input type="datetime-local" value={reviewedAt} onChange={e => setReviewedAt(e.target.value)} /></label>
        <label>Next review due<input type="datetime-local" value={nextDue} onChange={e => setNextDue(e.target.value)} /></label>
        <label className="span2">Notes<textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} maxLength={8000} /></label>
        <label className="span2">Crisis / relapse plan<textarea rows={2} value={crisisRelapse} onChange={e => setCrisisRelapse(e.target.value)} maxLength={8000} /></label>
        <label className="span2">Discharge plan<textarea rows={2} value={dischargePlan} onChange={e => setDischargePlan(e.target.value)} maxLength={8000} /></label>
      </div>

      <div className="php-plan-editor section-space">
        {goals.map((g, gi) => (
          <div key={g.goal_id} className="php-pe-goal">
            <div className="php-pe-head"><label className="php-pe-label">Goal: {g.goal_description}</label></div>
            <div className="php-pe-int-meta">
              <select value={g.overall_progress} onChange={e => setGoal(gi, { overall_progress: e.target.value })}>
                {Object.entries(OVERALL_PROGRESS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <input value={g.notes} onChange={e => setGoal(gi, { notes: e.target.value })} placeholder="Goal progress notes" />
            </div>
            {g.objectives.map((o, oi) => (
              <div key={o.objective_id} className="php-pe-obj">
                <div className="php-pe-head"><label className="php-pe-label">Objective: {o.objective_description}</label></div>
                <div className="php-pe-int-meta">
                  <select value={o.status} onChange={e => setObj(gi, oi, { status: e.target.value })}>
                    {Object.entries(OBJECTIVE_STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <input type="date" value={o.revised_target_date} onChange={e => setObj(gi, oi, { revised_target_date: e.target.value })} />
                </div>
                <div className="php-pe-int-meta">
                  <input value={o.progress} onChange={e => setObj(gi, oi, { progress: e.target.value })} placeholder="Progress" />
                  <input value={o.regression} onChange={e => setObj(gi, oi, { regression: e.target.value })} placeholder="Regression" />
                </div>
                <div className="php-pe-int-meta">
                  <input value={o.narrative} onChange={e => setObj(gi, oi, { narrative: e.target.value })} placeholder="Narrative" />
                  <input value={o.barriers} onChange={e => setObj(gi, oi, { barriers: e.target.value })} placeholder="Barriers" />
                </div>
              </div>
            ))}
          </div>
        ))}
        {goals.length === 0 && <p className="empty-copy">This plan has no goals to review.</p>}
      </div>

      <div className="php-plan-actions">
        <button className="button primary" disabled={busy}>{busy ? 'Recording…' : 'Record review'}</button>
        <button type="button" className="button ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
